import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import AIReviewStatisticsTemplate from 'components/templates/AIReviewStatistics';
import { WaitingManager } from 'components/organisms/CommonUI/WaitingPopup/WaitingManager';
import { ToastManager } from 'components/organisms/CommonUI/ToastPopup/ToastManager';
import { COLOR_SYSTEM } from 'components/design/design-system';
import { log, LogLevel } from 'utils/LogUtil';
import { getDefaultStartDate, getDefaultEndDate } from 'utils/dateUtils';
import { exportStatisticsToCSV } from 'utils/exportToCSV';
import {
  fetchAIReviewsStatistics,
  AIReviewStatisticsItem,
  StatisticsUnit,
} from 'services/api/adminStatistics';

const AIReviewStatisticsPage: React.FC = () => {
  // 조회 조건 상태
  const [startDate, setStartDate] = useState<string>(getDefaultStartDate());
  const [endDate, setEndDate] = useState<string>(getDefaultEndDate());
  const [unit, setUnit] = useState<StatisticsUnit>('week');
  const [simplifyPremiumRole, setSimplifyPremiumRole] = useState<boolean>(true);
  const [excludeUnspecified, setExcludeUnspecified] = useState<boolean>(false);
  const [groupByStatus, setGroupByStatus] = useState<boolean>(false);
  const [groupByPromptType, setGroupByPromptType] = useState<boolean>(false);
  const [groupByUserId, setGroupByUserId] = useState<boolean>(false);

  // 통계 데이터 상태
  const [statistics, setStatistics] = useState<AIReviewStatisticsItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  /**
   * 통계 조회
   */
  const handleSearch = useCallback(async () => {
    try {
      setLoading(true);
      WaitingManager.show({
        message: '통계 데이터를 불러오는 중입니다...',
      });

      const response = await fetchAIReviewsStatistics({
        startDate,
        endDate,
        unit,
        simplifyPremiumRole,
        groupByStatus,
        groupByPromptType,
        groupByUserId,
      });

      log(LogLevel.INFO, 'AI Review 통계 조회 성공', response);

      setStatistics(response.statistics);
    } catch (error) {
      log(LogLevel.ERROR, 'AI Review 통계 조회 실패', error);
      ToastManager.show({
        content: '통계 조회에 실패했습니다.',
        duration: 3000,
      });
    } finally {
      setLoading(false);
      WaitingManager.hide();
    }
  }, [
    startDate,
    endDate,
    unit,
    simplifyPremiumRole,
    groupByStatus,
    groupByPromptType,
    groupByUserId,
  ]);

  /**
   * 클라이언트 사이드 필터링: 미지정 제외
   */
  const filteredStatistics = useMemo(() => {
    if (!excludeUnspecified) {
      return statistics;
    }
    return statistics.filter((item) =>
      item.premiumRole !== '(미지정)' &&
      item.premiumRole !== 'Premium' &&
      item.premiumRole !== 'admin' &&
      item.premiumRole !== 'TEST'
    );
  }, [statistics, excludeUnspecified]);

  /**
   * CSV 다운로드
   */
  const handleCSVDownload = useCallback(() => {
    if (filteredStatistics.length === 0) {
      ToastManager.show({
        content: '다운로드할 데이터가 없습니다.',
        duration: 3000,
      });
      return;
    }

    try {
      exportStatisticsToCSV({
        statistics: filteredStatistics,
        startDate,
        endDate,
        unit,
      });

      ToastManager.show({
        content: 'CSV 파일이 다운로드되었습니다.',
        duration: 3000,
      });
    } catch (error) {
      log(LogLevel.ERROR, 'CSV 다운로드 실패', error);
      ToastManager.show({
        content: 'CSV 다운로드에 실패했습니다.',
        duration: 3000,
      });
    }
  }, [filteredStatistics, startDate, endDate, unit]);

  return (
    <PageContainer>
      <AIReviewStatisticsTemplate
        startDate={startDate}
        endDate={endDate}
        unit={unit}
        simplifyPremiumRole={simplifyPremiumRole}
        excludeUnspecified={excludeUnspecified}
        groupByStatus={groupByStatus}
        groupByPromptType={groupByPromptType}
        groupByUserId={groupByUserId}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onUnitChange={setUnit}
        onSimplifyPremiumRoleChange={setSimplifyPremiumRole}
        onExcludeUnspecifiedChange={setExcludeUnspecified}
        onGroupByStatusChange={setGroupByStatus}
        onGroupByPromptTypeChange={setGroupByPromptType}
        onGroupByUserIdChange={setGroupByUserId}
        onSearch={handleSearch}
        statistics={filteredStatistics}
        onCSVDownload={handleCSVDownload}
        loading={loading}
      />
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 24px;
  min-height: 100vh;
  background-color: ${COLOR_SYSTEM.get('Gray')?.[50]};
`;

export default AIReviewStatisticsPage;
